/*
 * RichTextEditor — TipTap-based WYSIWYG for email template bodies.
 *
 * Emits HTML that plays nicely with our variable placeholder syntax
 * (``{{variable}}``) — placeholders remain plain text nodes and travel
 * through Word / Outlook / Gmail without being re-parsed by the browser.
 *
 * Toolbar: Bold · Italic · Underline · H1/H2 · UL/OL · Link · Clear.
 *
 * The exposed API mirrors a controlled textarea: pass ``value`` (HTML
 * string), receive ``onChange`` with the new HTML, and ``onFocus``
 * fires when the editor gains focus so callers know where to insert
 * variable tokens.
 */
import React, { useEffect, useImperativeHandle, useRef } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import {
  Bold, Italic, Underline as UnderlineIcon, Link as LinkIcon,
  List, ListOrdered, Heading1, Heading2, Undo, Redo, Eraser,
} from 'lucide-react';

export interface RichTextEditorHandle {
  insertText: (text: string) => void;
  focus: () => void;
}

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  onFocus?: () => void;
  placeholder?: string;
  minHeight?: number;
  testId?: string;
}

/** Toolbar button. Small and simple — keeps focus on the editor. */
const TB: React.FC<{
  active?: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
  testId?: string;
}> = ({ active, onClick, title, children, testId }) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    className={
      'inline-flex items-center justify-center h-8 w-8 rounded-md text-raptor-navy transition-colors ' +
      (active
        ? 'bg-raptor-blue/15 text-raptor-navy border border-raptor-blue/40'
        : 'hover:bg-raptor-neutral/60 border border-transparent')
    }
    data-testid={testId}
  >{children}</button>
);

export const RichTextEditor = React.forwardRef<RichTextEditorHandle, RichTextEditorProps>(
  ({ value, onChange, onFocus, placeholder, minHeight = 240, testId }, ref) => {
    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          // Simpler default — the templates rarely need code blocks and
          // Gmail strips them anyway. Keeps the UI focused on the
          // things underwriters actually reach for.
          codeBlock: false,
          horizontalRule: false,
        }),
        Underline,
        Link.configure({
          openOnClick: false,
          autolink: true,
          HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
        }),
      ],
      content: value || '',
      onUpdate: ({ editor: ed }) => onChange(ed.getHTML()),
      onFocus: () => onFocus?.(),
      editorProps: {
        attributes: {
          class:
            'prose prose-sm max-w-none font-body text-raptor-navy focus:outline-none ' +
            // TipTap's contentEditable — matches Textarea look.
            'px-3 py-2',
          'data-placeholder': placeholder || '',
        },
      },
    });

    // Keep the editor in sync when the caller pushes an external change
    // (variable-chip inserts, template picker switch, etc.). Guard against
    // echo loops by only syncing when the outgoing HTML differs.
    const lastValueRef = useRef<string>(value);
    useEffect(() => {
      if (!editor) return;
      if (value === lastValueRef.current) return;
      lastValueRef.current = value;
      if (editor.getHTML() !== value) editor.commands.setContent(value || '', { emitUpdate: false });
    }, [value, editor]);

    // Expose imperative insertText so the VariablePicker can drop
    // ``{{token}}`` at the current caret position.
    useImperativeHandle(ref, () => ({
      insertText: (text: string) => {
        if (!editor) return;
        editor.chain().focus().insertContent(text).run();
      },
      focus: () => editor?.chain().focus().run(),
    }), [editor]);

    if (!editor) return null;

    const promptLink = (): void => {
      const previous = editor.getAttributes('link').href;
      const url = window.prompt('Link URL (leave blank to remove)', previous || 'https://');
      if (url === null) return;
      if (url.trim() === '') {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
        return;
      }
      editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
    };

    return (
      <div
        className="rte-wrap rounded-md border border-input bg-white focus-within:ring-2 focus-within:ring-raptor-blue/40 focus-within:border-raptor-blue overflow-hidden"
        data-testid={testId}
      >
        {/* Scoped styles so headings/lists/links render properly without a
            global typography plugin. Kept inline so the component is
            self-contained and portable across pages. */}
        <style>{`
          .rte-wrap .ProseMirror { min-height: ${minHeight}px; outline: none; padding: 10px 12px; font-size: 13.5px; line-height: 1.55; }
          .rte-wrap .ProseMirror h1 { font-size: 20px; font-weight: 700; margin: 0.5em 0 0.35em; letter-spacing: -0.01em; }
          .rte-wrap .ProseMirror h2 { font-size: 16px; font-weight: 700; margin: 0.6em 0 0.3em; }
          .rte-wrap .ProseMirror p { margin: 0.35em 0; }
          .rte-wrap .ProseMirror ul { list-style: disc; padding-left: 1.4em; margin: 0.35em 0; }
          .rte-wrap .ProseMirror ol { list-style: decimal; padding-left: 1.5em; margin: 0.35em 0; }
          .rte-wrap .ProseMirror li { margin: 0.15em 0; }
          .rte-wrap .ProseMirror li > p { margin: 0; }
          .rte-wrap .ProseMirror a { color: #0D63FF; text-decoration: underline; }
          .rte-wrap .ProseMirror u { text-decoration: underline; }
          .rte-wrap .ProseMirror strong { font-weight: 700; }
          .rte-wrap .ProseMirror em { font-style: italic; }
          .rte-wrap .ProseMirror.is-editor-empty:first-child::before {
            color: rgba(6, 25, 53, 0.35);
            content: attr(data-placeholder);
            float: left;
            height: 0;
            pointer-events: none;
          }
        `}</style>
        <div className="flex items-center gap-0.5 border-b border-border/60 bg-raptor-neutral/40 px-1.5 py-1 flex-wrap">
          <TB title="Bold (Ctrl+B)" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} testId="rte-bold"><Bold size={14} /></TB>
          <TB title="Italic (Ctrl+I)" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} testId="rte-italic"><Italic size={14} /></TB>
          <TB title="Underline (Ctrl+U)" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} testId="rte-underline"><UnderlineIcon size={14} /></TB>
          <div className="w-px h-5 bg-border/70 mx-1" />
          <TB title="Heading 1" active={editor.isActive('heading', { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} testId="rte-h1"><Heading1 size={14} /></TB>
          <TB title="Heading 2" active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} testId="rte-h2"><Heading2 size={14} /></TB>
          <div className="w-px h-5 bg-border/70 mx-1" />
          <TB title="Bullet list" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} testId="rte-ul"><List size={14} /></TB>
          <TB title="Numbered list" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} testId="rte-ol"><ListOrdered size={14} /></TB>
          <div className="w-px h-5 bg-border/70 mx-1" />
          <TB title="Add or edit link" active={editor.isActive('link')} onClick={promptLink} testId="rte-link"><LinkIcon size={14} /></TB>
          <div className="w-px h-5 bg-border/70 mx-1" />
          <TB title="Undo (Ctrl+Z)" onClick={() => editor.chain().focus().undo().run()} testId="rte-undo"><Undo size={14} /></TB>
          <TB title="Redo (Ctrl+Y)" onClick={() => editor.chain().focus().redo().run()} testId="rte-redo"><Redo size={14} /></TB>
          <div className="ml-auto" />
          <TB title="Clear formatting" onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()} testId="rte-clear"><Eraser size={14} /></TB>
        </div>
        <div style={{ minHeight }} className="text-sm rte-body">
          <EditorContent editor={editor as Editor} />
        </div>
      </div>
    );
  }
);
RichTextEditor.displayName = 'RichTextEditor';
